import { BASE_API_URL } from 'constants/env'
import { apiHeaders } from 'utils/api-headers'
import { store } from 'index'
import { handleErrors } from 'utils/handle-errors'
import { EntityIds, ParsedEntity } from 'interfaces/entity-resolver'

export class EntityApiService {
  /**
   * Fetch entity IDs from PNS based on parsed names
   */
  static async resolveEntityIds(parsed: ParsedEntity): Promise<EntityIds> {
    const entityIds: EntityIds = {} as EntityIds

    // Get terminal ID
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000)

      const terminalUrl = `${BASE_API_URL}/api/v4/phone_number_service/terminals?name=${parsed.terminalName}`

      let terminalResponse = await fetch(terminalUrl, {
        headers: apiHeaders(store.getState()),
        signal: controller.signal,
      })
      terminalResponse = handleErrors(terminalResponse)

      clearTimeout(timeoutId)

      if (!terminalResponse.ok) {
        if (terminalResponse.status === 404) {
          throw new Error(`Terminal '${parsed.terminalName}' not found`)
        } else if (terminalResponse.status >= 500) {
          throw new Error(`Server error while looking up Terminal`)
        } else {
          throw new Error(
            `Failed to fetch Terminal: ${terminalResponse.statusText}`
          )
        }
      }

      const terminals = await terminalResponse.json()
      if (!terminals.data || terminals.data.length === 0) {
        throw new Error(`Terminal '${parsed.terminalName}' not found`)
      }
      entityIds.terminalId = terminals.data[0].id
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`Timeout while looking up Terminal`)
      }
      throw error
    }

    // Get ansible ID (if we need it for the target entity)
    if (
      parsed.hasAnsible &&
      parsed.ansibleName &&
      ['ansible', 'mac-mini'].includes(parsed.entityType)
    ) {
      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 5000)

        let ansibleResponse = await fetch(
          `${BASE_API_URL}/api/v4/phone_number_service/ansibles?name=${parsed.ansibleName}`,
          {
            headers: apiHeaders(store.getState()),
            signal: controller.signal,
          }
        )
        ansibleResponse = handleErrors(ansibleResponse)

        clearTimeout(timeoutId)

        if (!ansibleResponse.ok) {
          if (ansibleResponse.status === 404) {
            throw new Error(`Ansible '${parsed.ansibleName}' not found`)
          } else if (ansibleResponse.status >= 500) {
            throw new Error(`Server error while looking up Ansible`)
          } else {
            throw new Error(
              `Failed to fetch Ansible: ${ansibleResponse.statusText}`
            )
          }
        }

        const ansibles = await ansibleResponse.json()
        if (!ansibles.data || ansibles.data.length === 0) {
          throw new Error(`Ansible '${parsed.ansibleName}' not found`)
        }
        entityIds.ansibleId = ansibles.data[0].id
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          throw new Error(`Timeout while looking up Ansible`)
        }
        throw error
      }
    }

    // Get Mac Mini ID (if we need it for the target entity)
    // Skip Mac Mini lookup for A0M0 patterns (A0M0 means "no mac mini")
    if (
      parsed.macMiniName &&
      !parsed.macMiniName.includes('A0M0') &&
      ['mac-mini'].includes(parsed.entityType)
    ) {
      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 5000)

        let macMiniResponse = await fetch(
          `${BASE_API_URL}/api/v4/phone_number_service/mac_minis?name=${parsed.macMiniName}`,
          {
            headers: apiHeaders(store.getState()),
            signal: controller.signal,
          }
        )
        macMiniResponse = handleErrors(macMiniResponse)

        clearTimeout(timeoutId)

        if (!macMiniResponse.ok) {
          if (macMiniResponse.status === 404) {
            throw new Error(`Mac Mini '${parsed.macMiniName}' not found`)
          } else if (macMiniResponse.status >= 500) {
            throw new Error(`Server error while looking up Mac Mini`)
          } else {
            throw new Error(
              `Failed to fetch Mac Mini: ${macMiniResponse.statusText}`
            )
          }
        }

        const macMinis = await macMiniResponse.json()
        if (!macMinis.data || macMinis.data.length === 0) {
          throw new Error(`Mac Mini '${parsed.macMiniName}' not found`)
        }
        entityIds.macMiniId = macMinis.data[0].id
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          throw new Error(`Timeout while looking up Mac Mini`)
        }
        throw error
      }
    }

    return entityIds
  }
}
